import React, { useState, useEffect, useMemo } from 'react';
import { User, CheckCircle, XCircle, RefreshCw, X, Star, Trophy } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { formatDate, formatMonthYear } from '../utils/dateFormat';
import { formatUiCurrencyCompact } from '../utils/uiFormat';
import AttendanceChart from '../components/AttendanceChart';
import { LifecycleBadge, OffDayLegend, offDayFilled } from '../components/LifecycleBadge';
import { UiTabs, UiViewToggle } from '../components/ui/UiTabs';
import SalaryCycleSummary from '../components/SalaryCycleSummary';

const OFF_DAY_TYPES = ['leave', 'holiday', 'absentee'];

const VIOLATION_COLORS = {
  late_arrival: 'bg-orange-100 text-orange-700',
  early_departure: 'bg-yellow-100 text-yellow-700',
  mobile_usage: 'bg-purple-100 text-purple-700',
  uniform: 'bg-indigo-100 text-indigo-700',
  gloves: 'bg-blue-100 text-blue-700',
  customer_service: 'bg-pink-100 text-pink-700',
  work_quality: 'bg-red-100 text-red-700',
  other: 'bg-gray-100 text-gray-600',
};

const SEVERITY_COLORS = {
  1: 'bg-yellow-100 text-yellow-700',
  2: 'bg-orange-100 text-orange-700',
  3: 'bg-red-100 text-red-700',
  4: 'bg-red-200 text-red-800',
};

const STATUS_STYLES = {
  on_time:  { bg: 'bg-green-50 border-green-300',  txt: 'text-green-800',  label: '✓',  badge: 'bg-green-100 text-green-700' },
  late:     { bg: 'bg-orange-50 border-orange-300', txt: 'text-orange-800', label: '⏰', badge: 'bg-orange-100 text-orange-700' },
  half_day: { bg: 'bg-red-50 border-red-300',       txt: 'text-red-800',    label: '½',  badge: 'bg-red-100 text-red-700' },
  leave:    { bg: 'bg-blue-50 border-blue-300',     txt: 'text-blue-800',   label: '🏖', badge: 'bg-blue-100 text-blue-700' },
  absent:   { bg: 'bg-gray-100 border-gray-300',    txt: 'text-gray-500',   label: '✗',  badge: 'bg-gray-100 text-gray-500' },
  non_paid_leave: { bg: 'bg-slate-100 border-slate-300', txt: 'text-slate-600', label: 'NP', badge: 'bg-slate-100 text-slate-600' },
};

const PAYROLL_STYLES = {
  salary: { marker: '✓', tone: 'text-green-700', bg: 'bg-green-50 border-green-300', label: 'Paid' },
  bonus: { marker: '★', tone: 'text-green-700', bg: 'bg-green-50 border-green-300', label: 'Bonus' },
  gift: { marker: '🎁', tone: 'text-green-700', bg: 'bg-green-50 border-green-300', label: 'Gift' },
  advance: { marker: '+', tone: 'text-green-700', bg: 'bg-green-50 border-green-300', label: 'Advance' },
  deduction: { marker: 'X', tone: 'text-rose-700', bg: 'bg-rose-50 border-rose-300', label: 'Deduction' },
  due: { marker: '?', tone: 'text-orange-700', bg: 'bg-orange-50 border-orange-300', label: 'Due' },
};

const PERIOD_OPTIONS = [
  { key: 'last_month', label: 'Last Month' },
  { key: 'month', label: 'This Month' },
  { key: 'year',  label: 'This Year' },
];


const AttendanceBar = ({ label, value, total, color }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className={`text-xs font-semibold px-2 py-0.5 rounded w-20 text-center ${color}`}>{label}</span>
      <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
        <div className={`h-4 rounded-full transition-all duration-500 ${color.replace('text-', 'bg-').split(' ')[0]}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-sm font-semibold text-gray-700 w-8 text-right">{value}</span>
      <span className="text-xs text-gray-400 w-10">({pct}%)</span>
    </div>
  );
};

const AttendanceModal = ({ records, initialFilter, onClose }) => {
  const [filter, setFilter] = useState(initialFilter || null);
  const filtered = filter ? records.filter(r => r.status === filter) : records;
  const BTNS = [
    { key: null,        label: 'All',      cls: 'bg-gray-100 text-gray-700' },
    { key: 'on_time',  label: 'On Time',  cls: 'bg-green-100 text-green-700' },
    { key: 'late',     label: 'Late',     cls: 'bg-orange-100 text-orange-700' },
    { key: 'half_day', label: 'Half Day', cls: 'bg-red-100 text-red-700' },
  ];
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
            Attendance Details
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
        </div>
        <div className="flex gap-2 p-3 border-b flex-wrap">
          {BTNS.map(btn => (
            <button
              key={String(btn.key)}
              onClick={() => setFilter(btn.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${filter === btn.key ? btn.cls + ' ring-2 ring-offset-1 ring-current' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}
            >{btn.label}</button>
          ))}
          <span className="ml-auto text-xs text-gray-400 self-center">{filtered.length} record(s)</span>
        </div>
        <div className="overflow-auto flex-1">
          {filtered.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No records found.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left text-gray-600 font-medium">Date</th>
                  <th className="px-4 py-2 text-left text-gray-600 font-medium">Arrival</th>
                  <th className="px-4 py-2 text-left text-gray-600 font-medium">Status</th>
                  <th className="px-4 py-2 text-left text-gray-600 font-medium">Late (min)</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map(r => {
                  const s = STATUS_STYLES[r.status];
                  return (
                    <tr key={r.date} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-700">{formatDate(r.date)}</td>
                      <td className="px-4 py-2 font-medium">{r.arrival_time || '—'}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${s?.badge || 'bg-gray-100 text-gray-500'}`}>
                          {r.status?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-gray-500">
                        {r.late_mins > 0 ? `+${r.late_mins}` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// StaffPortal can be used standalone (staff login) or embedded in manager view
// with staffId + managerView props to view any staff member's dashboard.
export default function StaffPortal({ staffId = null, managerView = false }) {
  const [data, setData]       = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod]   = useState('month');
  const [attModal, setAttModal] = useState(null);
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [dashboardTab, setDashboardTab] = useState('overview'); // overview | attendance | violations

  // Manager-only: arbitrary month/year navigation
  const todayDate = new Date();
  const [managerMonth, setManagerMonth] = useState({ y: todayDate.getFullYear(), m: todayDate.getMonth() + 1 }); // 1-indexed
  const [managerViewMode, setManagerViewMode] = useState('month'); // 'month' | 'year'

  const managerMonthStr = `${managerMonth.y}-${String(managerMonth.m).padStart(2, '0')}`;
  const isCurrentMonth = managerMonth.y === todayDate.getFullYear() && managerMonth.m === todayDate.getMonth() + 1;
  const isCurrentYear  = managerMonth.y === todayDate.getFullYear();

  const shiftManagerMonth = (delta) => {
    setManagerMonth(prev => {
      let m = prev.m + delta;
      let y = prev.y;
      if (m < 1) { m = 12; y--; }
      if (m > 12) { m = 1; y++; }
      // Don't allow future months
      const now = new Date();
      if (y > now.getFullYear() || (y === now.getFullYear() && m > now.getMonth() + 1)) return prev;
      return { y, m };
    });
  };

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const staffParam = staffId ? `&staff_id=${staffId}` : '';
      let periodParam = `period=${period}`;
      if (managerView) {
        if (managerViewMode === 'year') {
          periodParam = `period=year&custom_month=${managerMonth.y}-01`;
        } else {
          periodParam = `period=month&custom_month=${managerMonthStr}`;
        }
      }
      const [dashRes, settingsRes] = await Promise.all([
        api.get(`/staff/my-dashboard/?${periodParam}${staffParam}`),
        api.get('/settings/'),
      ]);
      setData(dashRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      if (error.response?.status === 404) {
        if (!managerView) toast.error('No staff profile linked to your account. Contact your administrator.');
      } else {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDashboard(); }, [period, staffId, managerMonth, managerViewMode]);
  useEffect(() => {
    api.get('/public-holidays/?page_size=500').then((res) => {
      setPublicHolidays(res.data.results || res.data || []);
    }).catch(() => {});
  }, []);

  const holidayMap = useMemo(() => {
    const map = {};
    publicHolidays.forEach((holiday) => {
      const start = holiday.start_date;
      const end = holiday.end_date || holiday.start_date;
      if (!start) return;
      let cursor = new Date(`${start}T12:00:00`);
      const endDate = new Date(`${end}T12:00:00`);
      while (!Number.isNaN(cursor.getTime()) && cursor <= endDate) {
        const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`;
        map[key] = holiday;
        cursor.setDate(cursor.getDate() + 1);
      }
    });
    return map;
  }, [publicHolidays]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500 space-y-3">
        <XCircle size={40} className="text-red-400" />
        <p className="font-medium">No staff profile linked to your account.</p>
        <p className="text-sm">Please contact your administrator to link your user account to a staff profile.</p>
      </div>
    );
  }

  const {
    staff, attendance, leaves, advances, bonuses = {}, gifts = {}, deductions = {}, monthly_salary, salary_due = {}, performance,
    period_start, period_end, month, daily_status = {}, perf_by_date = {}, payroll_by_date = {}, year_overview = {}, daily_records = [],
  } = data;
  const formatCurrency = (value) => formatUiCurrencyCompact(value);
  const showMonthlyDetails = managerView
    ? managerViewMode === 'month'
    : (period === 'month' || period === 'last_month');
  const showDeductions = showMonthlyDetails;

  const attendanceCounts = attendance || {};
  const attTotal = Number(
    attendanceCounts.total_days
    ?? (
      Number(attendanceCounts.on_time || 0)
      + Number(attendanceCounts.late || 0)
      + Number(attendanceCounts.half_day || 0)
      + Number(attendanceCounts.leave || 0)
      + Number(attendanceCounts.absent || 0)
      + Number(attendanceCounts.non_paid_leave || 0)
    )
  );
  const lateTotal = Number(attendanceCounts.late || 0) + Number(attendanceCounts.half_day || 0);
  const lateThreshold    = settings?.late_threshold_minutes || 15;
  const halfDayThreshold = settings?.half_day_threshold_minutes || 60;
  const monthLabel = month ? formatMonthYear(month) : '';
  const periodLabel = PERIOD_OPTIONS.find((option) => option.key === period)?.label || 'This Month';
  const bonusStarRecords = bonuses.star_records || [];
  const bonusStarSlots = Array.from({ length: 5 }, (_, index) => bonusStarRecords[index] || null);
  const bonusStars = Math.min(parseInt(bonuses.stars_earned || bonusStarRecords.length || 0, 10), 5);
  const starsRemaining = Math.max(0, 5 - bonusStars);
  const currentMonthLeaves = Number(leaves.current_month_days || 0);
  const allowedLeaves = Number(leaves.allowed_days ?? 4);
  const currentMonthExcessLeaves = Number(leaves.current_month_excess_days || 0);
  const currentMonthSalaryDeductedLeaves = Number(leaves.current_month_salary_deducted_days || 0);
  const currentMonthDeductionWaivedLeaves = Number(leaves.current_month_deduction_waived_days || 0);
  const lifetimeSalaryDeductedLeaves = Number(leaves.lifetime_salary_deducted_days || 0);
  const lifetimeDeductionWaivedLeaves = Number(leaves.lifetime_deduction_waived_days || 0);
  const pluralize = (value, singular, plural = `${singular}s`) => (Number(value) === 1 ? singular : plural);
  const leaveThisMonthText = (() => {
    if (currentMonthExcessLeaves <= 0) {
      return 'Within allowed leave for this month.';
    }
    const breakdown = [];
    if (currentMonthSalaryDeductedLeaves > 0) {
      breakdown.push(`${currentMonthSalaryDeductedLeaves} salary deducted`);
    }
    if (currentMonthDeductionWaivedLeaves > 0) {
      breakdown.push(`${currentMonthDeductionWaivedLeaves} deduction waived`);
    }
    const parts = [
      `${currentMonthExcessLeaves} excess ${pluralize(currentMonthExcessLeaves, 'leave day')} this month`,
    ];
    if (breakdown.length > 0) {
      parts.push(breakdown.join(' · '));
    }
    return parts.join(' · ');
  })();
  const leaveLifetimeText = `Lifetime: ${lifetimeSalaryDeductedLeaves} salary deducted · ${lifetimeDeductionWaivedLeaves} deduction waived`;
  const leaveCardLabel = showMonthlyDetails
    ? (period === 'last_month' ? 'Leaves Last Month' : 'Leaves This Month')
    : 'Leaves in Period';
  const leaveCardValue = showMonthlyDetails
    ? `${currentMonthLeaves}/${allowedLeaves}`
    : Number(leaves.period_total_days || 0);
  const leaveCardSub = showMonthlyDetails ? (
    <div className="space-y-0.5">
      <div>{leaveThisMonthText}</div>
      <div>{leaveLifetimeText}</div>
    </div>
  ) : (
    period_start && period_end
      ? `${formatDate(period_start)} to ${formatDate(period_end)}`
      : periodLabel
  );
  // Salary card = the Staff-page Salary-tab expandable, in card form (identical breakdown).
  const salarySummary = data.salary_summary || {};
  const sb = salarySummary.breakdown || {};
  const salaryIsDaily = (sb.wageType || salarySummary.wageType) === 'daily_wages';
  const salaryAsOf = salarySummary.asOfDate || period_end;
  const salaryNet = Number(salarySummary.runningSalaryBalance ?? salarySummary.unpaidAsOfToday ?? salary_due.payable ?? 0);

  // Performance card = the Performance-tab expandable buckets (Absentee / Minor / Major / Critical).
  const perfRecords = Array.isArray(performance) ? performance : [];
  const perfTotalDeducted = perfRecords.reduce((sum, r) => sum + (r.is_warning ? 0 : Number(r.penalty_amount || 0)), 0);
  const isOffDay = (r) => ['leave', 'holiday', 'absentee'].includes(r.violation_type);
  // Bucket a record the same way the Performance-tab expandable does.
  const bucketOf = (r) => r.violation_type === 'absentee' ? 'absentee'
    : Number(r.severity) === 4 ? 'critical'
    : Number(r.severity) === 3 ? 'major' : 'minor';
  const perfBucketAgg = (() => {
    const mk = () => ({ count: 0, deducted: 0, waived: 0, byType: {} });
    const m = { absentee: mk(), minor: mk(), major: mk(), critical: mk() };
    perfRecords.forEach(r => {
      if (r.violation_type === 'leave' || r.violation_type === 'holiday') return; // shown in Salary card
      const k = bucketOf(r);
      const amt = Number(r.penalty_amount || 0);
      m[k].count += 1;
      if (r.is_warning) m[k].waived += amt; else m[k].deducted += amt;
      const tName = r.violation_type_display || String(r.violation_type || '').replace(/_/g, ' ');
      if (!m[k].byType[tName]) m[k].byType[tName] = { count: 0, deducted: 0, waived: 0 };
      m[k].byType[tName].count += 1;
      if (r.is_warning) m[k].byType[tName].waived += amt; else m[k].byType[tName].deducted += amt;
    });
    return m;
  })();
  const perfBuckets = [
    { key: 'absentee', label: 'Absentee', dot: 'bg-indigo-400' },
    { key: 'minor', label: 'Minor', dot: 'bg-yellow-400' },
    { key: 'major', label: 'Major', dot: 'bg-red-400' },
    { key: 'critical', label: 'Critical', dot: 'bg-stone-800' },
  ].map(b => ({ ...b, ...perfBucketAgg[b.key] }));

  const bonusWindowMonths = bonuses.window_months || settings?.bonus_star_window_months || 9;
  const formatBonusMonth = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(`${String(dateStr).substring(0, 10)}T12:00:00`);
    if (Number.isNaN(d.getTime())) return String(dateStr).substring(0, 7);
    return formatMonthYear(d);
  };
  const deductionsSection = showDeductions ? (
    <div className="bg-white rounded-xl border shadow-sm p-5">
      <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
        Deductions
        <span className="ml-auto text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium">
          {formatCurrency(deductions.total_active || 0)}
        </span>
      </h2>
      {(deductions.records || []).length === 0 && (deductions.waived_records || []).length === 0 ? (
        <div className="flex items-center gap-2 py-5 justify-center text-green-600">
          <CheckCircle size={20} />
          <span className="text-sm font-medium">No deductions for this period.</span>
        </div>
      ) : (
        <div className="space-y-2">
          {(deductions.records || []).map(record => (
            <div key={`ded-${record.id}`} className="border rounded-lg px-3 py-2 text-sm">
              <div className="flex justify-between gap-3">
                <span className="font-medium text-gray-800">{formatDate(record.advance_date)}</span>
                <span className="font-bold text-red-600">{formatCurrency(record.amount || 0)}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">{record.reason || 'Deduction'}</p>
            </div>
          ))}
          {(deductions.waived_records || []).map(record => (
            <div key={`waived-${record.id}`} className="border rounded-lg px-3 py-2 text-sm bg-gray-50">
              <div className="flex justify-between gap-3">
                <span className="font-medium text-gray-700">{formatDate(record.date)} - {record.violation_type_display}</span>
                <span className="font-semibold text-gray-400 line-through">{formatCurrency(record.penalty_amount || 0)}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Warning issued, penalty waived.</p>
            </div>
          ))}
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className="space-y-6">
      {attModal !== null && (
        <AttendanceModal
          records={daily_records}
          initialFilter={attModal === 'all' ? null : attModal}
          onClose={() => setAttModal(null)}
        />
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Welcome, {staff.first_name}!</h1>
          <p className="text-sm text-gray-500">Your personal dashboard</p>
        </div>
        <button onClick={fetchDashboard} className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
          <RefreshCw size={15} /> Refresh
        </button>
      </div>

      {/* Profile card */}
      <div className="bg-white rounded-xl border shadow-sm p-5">
        <div className="flex items-start gap-4">
          <div className="bg-primary-100 rounded-full p-4">
            <User size={28} className="text-primary-600" />
          </div>
          <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><p className="text-xs text-gray-400">Staff Code</p><p className="font-semibold text-gray-800">{staff.staff_code}</p></div>
            <div><p className="text-xs text-gray-400">Full Name</p><p className="font-semibold text-gray-800">{staff.full_name || `${staff.first_name} ${staff.last_name}`}</p></div>
            <div><p className="text-xs text-gray-400">Designation</p><p className="font-semibold text-gray-800">{staff.designation || '—'}</p></div>
            <div><p className="text-xs text-gray-400">Department</p><p className="font-semibold text-gray-800">{staff.department_name || '—'}</p></div>
            <div><p className="text-xs text-gray-400">Status</p><p className={`font-semibold capitalize ${staff.employment_status === 'active' ? 'text-green-600' : 'text-gray-500'}`}>{staff.employment_status}</p></div>
            <div><p className="text-xs text-gray-400">Hire Date</p><p className="font-semibold text-gray-800">{formatDate(staff.hire_date)}</p></div>
            <div><p className="text-xs text-gray-400">Monthly Salary</p><p className="font-semibold text-gray-800">{formatCurrency(monthly_salary || 0)}</p></div>
          </div>
        </div>
      </div>

      {/* Period filter — manager gets full month/year navigation, staff gets limited options */}
      {managerView ? (
        <div className="flex flex-wrap items-center gap-2">
          {/* Month / Year toggle */}
          <UiViewToggle
            options={[{ value: 'month', label: 'Month' }, { value: 'year', label: 'Year' }]}
            value={managerViewMode}
            onChange={setManagerViewMode}
          />

          {/* Month navigator */}
          {managerViewMode === 'month' && (
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-2 py-1">
              <button
                onClick={() => shiftManagerMonth(-1)}
                className="p-1 hover:bg-gray-100 rounded-lg text-gray-500 text-sm font-bold"
              >‹</button>
              <span className="text-sm font-semibold text-gray-700 w-32 text-center">
                {MONTH_NAMES[managerMonth.m - 1]} {managerMonth.y}
              </span>
              <button
                onClick={() => shiftManagerMonth(1)}
                disabled={isCurrentMonth}
                className="p-1 hover:bg-gray-100 rounded-lg text-gray-500 text-sm font-bold disabled:opacity-30"
              >›</button>
              {!isCurrentMonth && (
                <button
                  onClick={() => setManagerMonth({ y: todayDate.getFullYear(), m: todayDate.getMonth() + 1 })}
                  className="text-xs text-primary-600 hover:text-primary-800 ml-1"
                >Today</button>
              )}
            </div>
          )}

          {/* Year navigator */}
          {managerViewMode === 'year' && (
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-2 py-1">
              <button
                onClick={() => setManagerMonth(prev => ({ ...prev, y: prev.y - 1 }))}
                className="p-1 hover:bg-gray-100 rounded-lg text-gray-500 text-sm font-bold"
              >‹</button>
              <span className="text-sm font-semibold text-gray-700 w-16 text-center">{managerMonth.y}</span>
              <button
                onClick={() => setManagerMonth(prev => ({ ...prev, y: Math.min(prev.y + 1, todayDate.getFullYear()) }))}
                disabled={isCurrentYear}
                className="p-1 hover:bg-gray-100 rounded-lg text-gray-500 text-sm font-bold disabled:opacity-30"
              >›</button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 w-fit">
          {PERIOD_OPTIONS.map(opt => (
            <button
              key={opt.key}
              onClick={() => setPeriod(opt.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${period === opt.key ? 'bg-white shadow text-primary-700' : 'text-gray-500 hover:text-gray-700'}`}
            >{opt.label}</button>
          ))}
        </div>
      )}

      {/* Summary cards — the Salary-tab & Performance-tab expandables, in card form */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Salary — mirrors the Salary-tab expandable */}
        <div className="bg-white rounded-xl border shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700"> Salary — {monthLabel || periodLabel}</h3>
            <span className="text-[11px] text-gray-400">Unpaid as on {salaryAsOf ? formatDate(salaryAsOf) : '—'}</span>
          </div>
          <SalaryCycleSummary
            summary={salarySummary}
            breakdown={sb}
            deductionRecords={deductions.records || []}
            asOfDate={salaryAsOf}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
          />
          <div className="hidden text-sm divide-y divide-gray-100">
            <div className="py-1.5">
              <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">a. {salaryIsDaily ? 'Wages (daily-wage)' : 'Salary (monthly)'}</div>
              {salaryIsDaily ? (
                <>
                  <div className="flex justify-between"><span className="text-gray-500">Daily wage rate</span><span>{formatCurrency(sb.dailyRate || 0)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Days present</span><span>{sb.daysPresent ?? 0} day(s)</span></div>
                  <div className="flex justify-between font-semibold"><span className="text-gray-600">Wage earned</span><span className="text-emerald-700">{formatCurrency(sb.grossEarned || 0)}</span></div>
                </>
              ) : (
                <>
                  <div className="flex justify-between"><span className="text-gray-500">Monthly salary</span><span>{formatCurrency((sb.dailyRate || 0) * 30)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Days present</span><span>{sb.daysPresent ?? 0} present · {sb.paidLeaves ?? 0} paid-leave</span></div>
                  <div className="flex justify-between font-semibold"><span className="text-gray-600">Salary earned</span><span className="text-emerald-700">{formatCurrency(sb.grossEarned || 0)}</span></div>
                </>
              )}
            </div>
            <div className="py-1.5">
              <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">b. Leaves & Off-days</div>
              <div className="flex justify-between"><span className="text-gray-500">Holidays</span><span>{sb.holidays ?? 0} day(s)</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Paid leaves</span><span>{sb.paidLeaves ?? 0} day(s)</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Unpaid leaves</span><span>{sb.unpaidLeaves ?? 0} day(s)</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Waived absentee</span><span>{sb.waivedAbsentee ?? 0} day(s)</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Deducted absentee</span><span>{sb.deductedAbsentee ?? 0} day(s)</span></div>
            </div>
            <div className="py-1.5">
              <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">c. Deductions</div>
              <div className="flex justify-between"><span className="text-gray-500">Total deductions</span><span className="text-rose-700 font-semibold">{formatCurrency(salarySummary.totalDeductions || 0)}</span></div>
            </div>
            <div className="py-1.5">
              <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">d. Advances</div>
              <div className="flex justify-between"><span className="text-gray-500">Advances in cycle</span><span className="text-green-700">{formatCurrency(salarySummary.totalAdvance || 0)}</span></div>
            </div>
            <div className="py-1.5">
              <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">e. Bonus / Gift</div>
              <div className="flex justify-between"><span className="text-gray-500">Bonus in cycle</span><span className="text-green-700">{formatCurrency(salarySummary.totalBonus || 0)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">🎁 Gift in cycle</span><span className="text-green-700">{formatCurrency(salarySummary.totalGift || 0)}</span></div>
            </div>
            <div className="py-1.5">
              <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">f. Payable Salary (a - c - d)</div>
              <div className="flex justify-between"><span className="text-gray-500">Payable salary before carry</span><span className="font-semibold text-orange-700">{formatCurrency(salarySummary.payableSalaryBeforeCarry || 0)}</span></div>
            </div>
            <div className="py-1.5">
              <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">g. Over / Underpayment Carried Forward</div>
              <div className="flex justify-between"><span className="text-gray-500">Over / underpayment carried forward</span><span className={Number(salarySummary.signedCarryForward || 0) >= 0 ? 'text-green-700' : 'text-amber-700'}>{Number(salarySummary.signedCarryForward || 0) > 0 ? '+' : Number(salarySummary.signedCarryForward || 0) < 0 ? '−' : ''}{formatCurrency(Math.abs(Number(salarySummary.signedCarryForward || 0)))}</span></div>
            </div>
            <div className="py-1.5">
              <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">h. Total Payable (f - g)</div>
              <div className="flex justify-between"><span className="font-bold text-gray-700">Total payable</span><span className="font-bold text-orange-700">{formatCurrency(salaryNet)}</span></div>
            </div>
          </div>
        </div>
        {/* Performance — mirrors the Performance-tab expandable buckets */}
        <div className="bg-white rounded-xl border shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700"> Performance — {monthLabel || periodLabel}</h3>
            <span className="text-[11px] text-gray-400">{perfRecords.length} record{perfRecords.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="space-y-2 mb-3">
            {perfBuckets.filter(b => b.count > 0).length === 0 ? (
              <div className="text-gray-400 text-sm py-2">No violations in this period 🎉</div>
            ) : perfBuckets.filter(b => b.count > 0).map(b => (
              <div key={b.key} className="border rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50">
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-700"><span className={`w-2 h-2 rounded-full ${b.dot}`} />{b.label}</span>
                  <span className="text-base font-bold text-gray-800">{b.count}</span>
                </div>
                <div className="divide-y divide-gray-100">
                  {Object.entries(b.byType).map(([t, info]) => (
                    <div key={t} className="flex items-center gap-2 px-3 py-1 text-[11px]">
                      <span className="text-gray-600 capitalize flex-1 truncate">{t} <span className="text-gray-300">×{info.count}</span></span>
                      <span className="text-gray-400 whitespace-nowrap">waived {formatCurrency(info.waived)}</span>
                      <span className="text-rose-700 font-semibold whitespace-nowrap">deducted {formatCurrency(info.deducted)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between border-t pt-2 text-sm"><span className="font-bold text-gray-700">Total deducted</span><span className="font-bold text-rose-700">{perfTotalDeducted > 0 ? `-${formatCurrency(perfTotalDeducted)}` : formatCurrency(0)}</span></div>
        </div>
      </div>

      <div className="bg-white rounded-xl border shadow-sm p-5">
        <h2 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <span aria-hidden="true">🎁</span> Gift Received
          <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">{formatCurrency(gifts.this_month_total || 0)}</span>
        </h2>
        {(gifts.records || []).length === 0 ? (
          <p className="text-sm text-gray-400">No gifts in this period.</p>
        ) : (
          <div className="border rounded-lg divide-y overflow-hidden">
            {gifts.records.slice(0, 5).map(record => (
              <div key={record.id} className="flex justify-between gap-3 px-3 py-2 text-sm">
                <span className="text-gray-700">{formatDate(record.advance_date)}</span>
                <span className="font-semibold text-green-700">{formatCurrency(record.amount || 0)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Calendar View — month or year depending on period */}
      <div className="bg-white rounded-xl border shadow-sm p-5">
        <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
          Bonus Received
          <span className="ml-auto text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-medium">
            {formatCurrency(bonuses.total_received || 0)}
          </span>
        </h2>
        <div className="flex items-start gap-3 mb-3 flex-wrap">
          {bonusStarSlots.map((record, index) => (
            <div key={index} className="w-14 text-center">
              <Star size={28} className={`mx-auto ${record ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
              <div className={`mt-1 text-[10px] leading-tight ${record ? 'text-yellow-700 font-medium' : 'text-gray-300'}`}>
                {record ? formatBonusMonth(record.advance_date) : '--'}
              </div>
            </div>
          ))}
          <div className="w-16 text-center">
            <Trophy size={30} className={`mx-auto ${bonusStars >= 5 ? 'text-yellow-500 fill-yellow-100' : 'text-gray-300'}`} />
            <div className="mt-1 text-[10px] leading-tight text-gray-400">Target</div>
          </div>
        </div>
        <p className="text-sm text-gray-600">
          {bonusStars >= 5 ? 'Trophy target achieved. Certificate and trophy of performance earned.' : `${starsRemaining} more star${starsRemaining !== 1 ? 's' : ''} for trophy target.`}
          <span className="block text-xs text-gray-400 mt-1">Counting bonuses from the last {bonusWindowMonths} month{bonusWindowMonths !== 1 ? 's' : ''}.</span>
        </p>
        {(bonuses.records || []).length > 0 && (
          <div className="mt-4 border rounded-lg divide-y overflow-hidden">
            {bonuses.records.slice(0, 5).map(record => (
              <div key={record.id} className="flex justify-between gap-3 px-3 py-2 text-sm">
                <span className="text-gray-700">{formatDate(record.advance_date)}</span>
                <span className="font-semibold text-yellow-700">{formatCurrency(record.amount || 0)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dashboard section tabs */}
      <UiTabs
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'attendance', label: 'Attendance' },
          { id: 'violations', label: 'Violations' },
        ]}
        active={dashboardTab}
        onChange={setDashboardTab}
      />

      {dashboardTab === 'overview' && (period === 'year' ? (() => {
        const calYr = period_start
          ? new Date(`${String(period_start).substring(0, 10)}T12:00:00`).getFullYear()
          : new Date().getFullYear();
        const todayYM = new Date().toISOString().substring(0, 7);
        const MO_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        const yearGrid = year_overview || {};
        return (
          <div className="bg-white rounded-xl border shadow-sm p-5">
            <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
              {calYr} — Year Overview
            </h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {MO_SHORT.map((mName, mi) => {
                const ym = `${calYr}-${String(mi + 1).padStart(2, '0')}`;
                const agg = yearGrid[ym] || {};
                const att = agg.attendance || {};
                const isPast = ym <= todayYM;
                const isCurrentMonth = ym === todayYM;
                const total = Number(att.total_days || 0);
                const lateDays = Number(att.late || 0) + Number(att.half_day || 0);
                const missedDays = Number(att.absent || 0) + Number(att.non_paid_leave || 0);
                const payrollTotal = Number(agg.paidTotal || 0) + Number(agg.advanceTotal || 0) + Number(agg.bonusTotal || 0) + Number(agg.giftTotal || 0) + Number(agg.deductionTotal || 0) + Number(agg.dueTotal || 0);
                let bg = 'bg-gray-50 border-gray-200', txt = 'text-gray-400';
                if (isPast && total > 0) {
                  if (lateDays === 0 && missedDays === 0) { bg = 'bg-green-50 border-green-200'; txt = 'text-green-800'; }
                  else if (lateDays + missedDays < total) { bg = 'bg-orange-50 border-orange-200'; txt = 'text-orange-800'; }
                  else { bg = 'bg-red-50 border-red-200'; txt = 'text-red-800'; }
                }
                return (
                  <div key={ym} className={`${bg} border rounded-xl p-3 ${isCurrentMonth ? 'ring-2 ring-primary-500' : ''} ${!isPast ? 'opacity-40' : ''}`}>
                    <div className={`font-bold text-sm ${txt}`}>{mName}</div>
                    {isPast && (total > 0 || payrollTotal > 0 || Number(agg.performanceCount || 0) > 0) && (
                      <div className="mt-1 space-y-1">
                        {total > 0 && (
                          <div className="text-[11px] text-gray-600">
                            Days {total} · ✓ {att.on_time || 0} · ⏰ {lateDays} · 🏖 {att.leave || 0} · ✗ {missedDays}
                          </div>
                        )}
                        {Number(agg.performanceCount || 0) > 0 && (
                          <div className="text-[11px] text-red-700">
                            Violations {agg.performanceCount}
                            {Number(agg.performanceDeductions || 0) > 0 ? ` · -${formatCurrency(agg.performanceDeductions)}` : ''}
                          </div>
                        )}
                        <div className="space-y-0.5">
                          {Number(agg.paidTotal || 0) > 0 && <div className="text-[11px] text-green-700">✓ {formatCurrency(agg.paidTotal)}</div>}
                          {Number(agg.bonusTotal || 0) > 0 && <div className="text-[11px] text-green-700">★ {formatCurrency(agg.bonusTotal)}</div>}
                          {Number(agg.giftTotal || 0) > 0 && <div className="text-[11px] text-green-700">🎁 {formatCurrency(agg.giftTotal)}</div>}
                          {Number(agg.advanceTotal || 0) > 0 && <div className="text-[11px] text-green-700">+ {formatCurrency(agg.advanceTotal)}</div>}
                          {Number(agg.deductionTotal || 0) > 0 && <div className="text-[11px] text-rose-700">X {formatCurrency(agg.deductionTotal)}</div>}
                          {Number(agg.dueTotal || 0) > 0 && <div className="text-[11px] text-orange-700">? {formatCurrency(agg.dueTotal)}</div>}
                        </div>
                      </div>
                    )}
                    {isPast && total === 0 && payrollTotal === 0 && Number(agg.performanceCount || 0) === 0 && <div className="text-xs text-gray-400 mt-1">No records</div>}
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t text-xs text-gray-400">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-100 border border-green-300 inline-block" />All On Time</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-100 border border-orange-300 inline-block" />Some Late</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-100 border border-red-300 inline-block" />Mostly Missed/Late</span>
              {Object.values(PAYROLL_STYLES).map((style) => (
                <span key={style.label} className="flex items-center gap-1">
                  <span className={`w-3 h-3 rounded border inline-block ${style.bg}`} />
                  <span className={`font-semibold ${style.tone}`}>{style.marker}</span>
                  {style.label}
                </span>
              ))}
            </div>
          </div>
        );
      })() : month && (() => {
        const [calYr, calMo] = month.substring(0, 7).split('-').map(Number);
        const daysInMonth = new Date(calYr, calMo, 0).getDate();
        const firstDow = new Date(calYr, calMo - 1, 1).getDay();
        const firstMon = firstDow === 0 ? 6 : firstDow - 1;
        const todayStr = new Date().toISOString().substring(0, 10);
        const cells = [];
        for (let i = 0; i < firstMon; i++) cells.push(null);
        for (let d = 1; d <= daysInMonth; d++) cells.push(d);
        return (
          <div className="bg-white rounded-xl border shadow-sm p-5">
            <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
              {monthLabel} — Overview
            </h2>
            <div className="grid grid-cols-7 gap-1 mb-1">
              {['Mo','Tu','We','Th','Fr','Sa','Su'].map(d => (
                <div key={d} className="text-center text-xs font-semibold text-gray-400 py-1">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((day, i) => {
                if (!day) return <div key={`e${i}`} />;
                const ds = `${calYr}-${String(calMo).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                const status = daily_status[ds];
                const holiday = holidayMap[ds];
                const isFuture = ds > todayStr;
                const isToday  = ds === todayStr;
                const style    = status ? STATUS_STYLES[status] : null;
                const viol     = perf_by_date[ds] || [];
                const payInfo  = payroll_by_date[ds] || {};
                const payrollLines = [
                  ['salary', payInfo.paidTotal],
                  ['bonus', payInfo.bonusTotal],
                  ['gift', payInfo.giftTotal],
                  ['advance', payInfo.advanceTotal],
                  ['due', payInfo.dueTotal],
                  // 'deduction' intentionally omitted — deductions now show as the per-violation lines below.
                ].filter(([, amount]) => Number(amount || 0) > 0);
                const primaryPayrollType = ['salary', 'bonus', 'gift', 'advance', 'deduction', 'due']
                  .find((type) => Number(
                    type === 'salary'
                      ? payInfo.paidTotal
                      : payInfo[`${type}Total`]
                  || 0) > 0);
                const payrollStyle = primaryPayrollType ? PAYROLL_STYLES[primaryPayrollType] : null;
                const cellClass = payrollStyle?.bg || (style ? style.bg : holiday ? 'bg-amber-50 border-amber-300' : 'bg-gray-50 border-gray-100');
                return (
                  <div key={ds} className={`border rounded-lg p-1 min-h-[76px] ${holiday ? 'ring-1 ring-amber-300' : ''} ${isToday ? 'ring-2 ring-primary-500' : ''} ${cellClass}`}>
                    <div className={`text-xs font-bold ${style ? style.txt : payrollStyle ? payrollStyle.tone : 'text-gray-300'}`}>{day}</div>
                    {holiday && <div className="text-[9px] text-amber-700 font-semibold leading-tight truncate">{holiday.name}</div>}
                    {payrollLines.length > 0 && (
                      <div className="space-y-0.5 mt-0.5">
                        {payrollLines.map(([type, amount]) => {
                          const rowStyle = PAYROLL_STYLES[type];
                          return (
                            <div key={type} className={`text-[10px] leading-tight font-semibold truncate ${rowStyle.tone}`}>
                              {rowStyle.marker} {formatCurrency(amount)}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* Off-days (Leave/Holiday/Absentee) icons with the day-off name on the same line. */}
                    {viol.filter(v => OFF_DAY_TYPES.includes(v.violation_type)).map((v, vi) => (
                      <LifecycleBadge key={`off-${vi}`} type={v.violation_type} name={{ leave: 'Leave', holiday: 'Holiday', absentee: 'Absentee' }[v.violation_type]} filled={offDayFilled(v.violation_type, v.is_warning)} size={13} />
                    ))}
                    {/* Every other violation: the penalty name next to the dot (staff is obvious — it's
                        their own dashboard), with the deducted amount appended when there is one. */}
                    {(() => {
                      const others = viol.filter(v => !OFF_DAY_TYPES.includes(v.violation_type));
                      const dotCls = (v) => v.severity >= 3 ? 'bg-red-500' : Number(v.severity) === 2 ? 'bg-orange-400' : 'bg-yellow-400';
                      return others.slice(0, 10).map((v, vi) => {
                        const amt = !v.is_warning ? Number(v.penalty_amount || 0) : 0;
                        return (
                          <div key={`v-${vi}`} className="flex items-center gap-1 mt-0.5 text-[9px] leading-tight">
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 inline-block ${dotCls(v)}`} />
                            <span className="truncate text-gray-600">{v.violation_type_display || String(v.violation_type || '').replace(/_/g, ' ')}</span>
                            {amt > 0 && <span className="text-red-700 font-semibold shrink-0">(-{formatCurrency(amt)})</span>}
                          </div>
                        );
                      });
                    })()}
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t text-xs text-gray-400">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-100 border border-green-300 inline-block" />On Time</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-100 border border-orange-300 inline-block" />Late</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-100 border border-red-300 inline-block" />Half Day</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-100 border border-blue-300 inline-block" />Leave</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-100 border border-slate-300 inline-block" />Non-paid leave</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-100 border border-gray-300 inline-block" />Absent</span>
              <OffDayLegend />
              {Object.values(PAYROLL_STYLES).map((style) => (
                <span key={style.label} className="flex items-center gap-1">
                  <span className={`w-3 h-3 rounded border inline-block ${style.bg}`} />
                  <span className={`font-semibold ${style.tone}`}>{style.marker}</span>
                  {style.label}
                </span>
              ))}
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400 inline-block" />Violation</span>
            </div>
          </div>
        );
      })())}

      {/* ── Attendance tab ── */}
      {dashboardTab === 'attendance' && (
      <div className="bg-white rounded-xl border shadow-sm p-5">
        <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
          Attendance
          <span className="ml-auto text-xs text-gray-400 font-normal">{attTotal} covered day(s)</span>
        </h2>
        {attTotal === 0 ? (
          <p className="text-sm text-gray-400 py-4 text-center">No attendance records for this period.</p>
        ) : (
          <div className="space-y-3">
            <AttendanceBar label="On Time"  value={attendanceCounts.on_time || 0}  total={attTotal} color="bg-green-100 text-green-700" />
            <AttendanceBar label="Late"     value={attendanceCounts.late || 0}     total={attTotal} color="bg-orange-100 text-orange-700" />
            <AttendanceBar label="Half Day" value={attendanceCounts.half_day || 0} total={attTotal} color="bg-red-100 text-red-700" />
            <AttendanceBar label="Leave" value={attendanceCounts.leave || 0} total={attTotal} color="bg-blue-100 text-blue-700" />
            <AttendanceBar label="Absent" value={attendanceCounts.absent || 0} total={attTotal} color="bg-gray-100 text-gray-700" />
            <AttendanceBar label="Non-paid" value={attendanceCounts.non_paid_leave || 0} total={attTotal} color="bg-slate-100 text-slate-700" />
          </div>
        )}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-center">
          <div className="bg-green-50 rounded-lg p-3 cursor-pointer hover:bg-green-100 transition" onClick={() => setAttModal('on_time')}>
            <p className="text-xl font-bold text-green-700">{attendanceCounts.on_time || 0}</p>
            <p className="text-xs text-green-600 font-medium">On Time</p>
            <p className="text-xs text-green-500 mt-1">View →</p>
          </div>
          <div className="bg-orange-50 rounded-lg p-3 cursor-pointer hover:bg-orange-100 transition" onClick={() => setAttModal('late')}>
            <p className="text-xl font-bold text-orange-700">{attendanceCounts.late || 0}</p>
            <p className="text-xs text-orange-600 font-medium">Late (&gt;{lateThreshold}m)</p>
            <p className="text-xs text-orange-500 mt-1">View →</p>
          </div>
          <div className="bg-red-50 rounded-lg p-3 cursor-pointer hover:bg-red-100 transition" onClick={() => setAttModal('half_day')}>
            <p className="text-xl font-bold text-red-700">{attendanceCounts.half_day || 0}</p>
            <p className="text-xs text-red-600 font-medium">Half Day (&gt;{halfDayThreshold}m)</p>
            <p className="text-xs text-red-500 mt-1">View →</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-xl font-bold text-blue-700">{attendanceCounts.leave || 0}</p>
            <p className="text-xs text-blue-600 font-medium">Leave</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xl font-bold text-gray-700">{attendanceCounts.absent || 0}</p>
            <p className="text-xs text-gray-600 font-medium">Absent</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xl font-bold text-slate-700">{attendanceCounts.non_paid_leave || 0}</p>
            <p className="text-xs text-slate-600 font-medium">Non-paid</p>
          </div>
        </div>
        {showMonthlyDetails && (
          <button
            onClick={() => setAttModal('all')}
            className="mt-4 w-full text-sm text-primary-600 border border-primary-200 rounded-lg py-2 hover:bg-primary-50 transition"
          >View All Attendance Records</button>
        )}
      </div>
      )}

      {/* Attendance Timeline Chart */}
      {dashboardTab === 'attendance' && showMonthlyDetails && (
        <AttendanceChart data={data} calendarMonth={month} />
      )}

      {/* ── Violations tab ── */}
      {dashboardTab === 'violations' && showMonthlyDetails && (
      <div className="bg-white rounded-xl border shadow-sm p-5">
        <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
          Violations
          {performance.length > 0 && (
            <span className="ml-auto text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium">
              {performance.length} record(s)
            </span>
          )}
        </h2>
        {performance.length === 0 ? (
          <div className="flex items-center gap-2 py-6 justify-center text-green-600">
            <CheckCircle size={20} />
            <span className="text-sm font-medium">No violations for this period. Keep it up!</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-left">Violation</th>
                  <th className="px-4 py-2 text-left">Severity</th>
                  <th className="px-4 py-2 text-left">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {performance.map(p => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2">{formatDate(p.date)}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${VIOLATION_COLORS[p.violation_type] || 'bg-gray-100 text-gray-600'}`}>
                        {p.violation_type_display}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${SEVERITY_COLORS[p.severity] || 'bg-gray-100 text-gray-600'}`}>
                        {p.severity_display}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-500 max-w-xs truncate">{p.description || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}
      {dashboardTab === 'violations' && deductionsSection}
    </div>
  );
}
